import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useToken } from '../common/helper';
import {
  getActiveToken,
  isPreviewTabActive,
} from '@/common/helper/tokenStorage';
import './Layout.scss';
import ResponsiveHeader from './Header/ResponsiveHeader';
import CustomSidebar from './CustomSidebar';
import postscribe from 'postscribe';
import theme from '../assets/theme';
import useStoreInitializer from '@/hooks/useStoreInitializer';
import { usePreviewIdleTimeout } from '@/hooks';
import { isZoomlogiSupport } from '@/common/utils/utils';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux-toolkit/store';
import { HelpCenterPanel } from '@/layout/AppHelpCenter';
import type { HelpOpenSource } from '@/layout/AppHelpCenter/types';

declare global {
  interface Window {
    amazon_connect?: unknown;
    openZoomlogiChat?: () => void;
  }
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

function DashboardLayout({
  children,
}: DashboardLayoutProps): React.JSX.Element {
  const { decodedUserToken, getPreviewState } = useToken();
  const decodedToken = decodedUserToken();
  const { isPreview } = getPreviewState();
  const shouldShowRolePreview = useSelector(
    (state: RootState) => state.preview.shouldShowRolePreview
  );
  const showPreview = isPreview || shouldShowRolePreview;

  // Auto-exits an active preview session after 30 minutes of inactivity;
  // no-op when no preview session is active.
  usePreviewIdleTimeout();

  const location = useLocation();
  const isShipmentRoute = location.pathname
    .toLowerCase()
    .startsWith('/shipment');

  const companyName = decodedToken?.company_name?.toLowerCase();
  const initializeStore = companyName !== 'zoomlogi support';
  const storage = isPreviewTabActive() ? sessionStorage : localStorage;
  const persistedState = storage.getItem('persist:root');
  const activeToken = getActiveToken();
  useStoreInitializer(activeToken, persistedState, initializeStore);

  const injectedRef = useRef(false);

  // Help center or Insight Chat, never
  // both. Owned here since Help and the header (which holds Insight Chat) are
  // both children of this layout. Insight Chat may carry a question handed
  // off from Help, sent in a new chat.
  const [sidePanel, setSidePanel] = useState<{
    name: 'help' | 'insight';
    question?: string;
    // How Help was opened, for telemetry.
    source?: HelpOpenSource;
  } | null>(null);
  const helpCenterOpen = sidePanel?.name === 'help';
  // Data modal (sidebar's last item) state, shared so Help shows Data's tasks.
  const [dataModalOpen, setDataModalOpen] = useState(false);

  const toggleHelpCenter = () =>
    setSidePanel((prev) =>
      prev?.name === 'help' ? null : { name: 'help', source: 'button' }
    );
  // Close only the named surface, so a late close can't shut the other one.
  const closeSidePanel = (name: 'help' | 'insight') =>
    setSidePanel((prev) => (prev?.name === name ? null : prev));
  const openInsightChat = (question?: string) =>
    setSidePanel({ name: 'insight', question });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '?') return;

      const target = event.target as HTMLElement | null;
      const isTextInput =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (isTextInput) return;

      setSidePanel((prev) =>
        prev?.name === 'help' ? null : { name: 'help', source: 'shortcut' }
      );
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (
      isZoomlogiSupport(decodedToken?.company_name) ||
      decodedToken?.chatbot === false
    ) {
      return;
    }
    if (injectedRef.current) return;

    const rootEl = document.getElementById('root');
    if (!rootEl) {
      console.error('Target element #root not found');
      return;
    }

    const name = decodedToken?.name || 'Guest';
    const company = decodedToken?.company_name || 'Unknown';

    const scriptContent = `
<script type="text/javascript">
  (function (w, d, x, id) {
    var s = d.createElement('script');
    s.src = 'https://zoomlogiconnect.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js';
    s.async = 1;
    s.id = id;
    d.getElementsByTagName('head')[0].appendChild(s);
    w[x] = w[x] || function () { (w[x].ac = w[x].ac || []).push(arguments); };
  })(window, document, 'amazon_connect', 'c10b159f-c584-4f49-8192-66738e4be615');

  amazon_connect('styles', {
    iconType: 'CHAT_VOICE',
    openChat:  { color: '#ffffff', backgroundColor: 'var(--light-blue)' },
    closeChat: { color: '#ffffff', backgroundColor: 'var(--light-blue)' },
    headerConfig: { headerMessage: 'Zoomlogi', headerColorHex: '#266ef1' },
    logoConfig:   { sourceUrl: '' }
  });

  amazon_connect('snippetId',
    'QVFJREFIZ2ZYaENvQWJCb1ZtYmxRNlFMNVJYMlhab3BKY1p0RnRGQ2pJNGhxdnBvemdIdEZVL2xnY0dRRzZrNjRPN2NlcnVVQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNTmFPaUlFdXRWMkkwZUdUTEFnRVFnQ3RZZEZmdFdqcjd0RjNMczd4ZFY0bitWN1l2d1I2WXQ5azNsd3BMZW52K0pZc3dKT3hTNzJSTStNUnM6Om9oVElDQTBndVl6YXJKQlJVZ2pSRVNXUTZzS0UwT0tHUHR6N2xVaEhjaHVYNHhiUkE5UGs4Y1FtMGJ5NG1iK0gya2s2bnl5dlBhQmVVQkl5Wnhsd0xKOENld0lLQmVjZnByS2lVT3dIS0dreUNJNjJBN2hQUG5iV1pveXdnbHVWTUJzTnV0S0xZQ3NKdThmdklMMjRwa2JQK3dGcm9raz0='
  );

  amazon_connect('customerMetadata', {
    name: ${JSON.stringify(name)},
    company: ${JSON.stringify(company)},
    companyname: ${JSON.stringify(company)}
  });

  amazon_connect('supportedMessagingContentTypes', [
    'text/plain',
    'text/markdown',
    'application/vnd.amazonaws.connect.message.interactive',
    'application/vnd.amazonaws.connect.message.interactive.response'
  ]);
(function attachClickAwayClose() {
  function isWidgetOpen() {
    return !!document.querySelector('#amazon-connect-widget-frame.show');
  }

  function closeWidget() {
    const closeBtn = document.getElementById('amazon-connect-close-widget-button');
    if (closeBtn) closeBtn.click();
  }

  document.addEventListener('mousedown', function (e) {
    const frame = document.getElementById('amazon-connect-widget-frame');
    if (!frame) return;

    if (!isWidgetOpen()) return;

    const clickedInsideWidget = frame.contains(e.target);
    if (!clickedInsideWidget) closeWidget();
  }, true);
})();

  amazon_connect('viewConfig', JSON.stringify({
    name: 'Contact Form',
    id: 'contact-form-embedded',
    arn: 'arn:aws:connect:us-east-1:050451381240:instance/3c1da07a-b3e7-45f0-b335-134d3d4b1a61/view/004f3a4d-78b1-4d0a-8351-0e52fbf20be7:$LATEST',
    type: 'CUSTOMER_MANAGED',
    status: 'PUBLISHED',
    content: {
      template: {
        Head: { Configuration: { Layout: { Columns: '12' } }, Title: 'Contact Form' },
        Body: [
          {
            _id: 'Form',
            Type: 'Form',
            Props: { HideBorder: false },
            Content: [
              {
                _id: 'FirstName',
                Type: 'FormInput',
                Props: {
                  Label: 'First name',
                  Name: 'first-name',
                  InputType: 'text',
                  Required: true,
                  DefaultValue: ${JSON.stringify(name)}
                },
                Content: []
              },
              {
                _id: 'Company',
                Type: 'FormInput',
                Props: {
                  Label: 'Company',
                  Name: 'company',
                  InputType: 'text',
                  Required: true,
                  DefaultValue: ${JSON.stringify(company)}
                },
                Content: []
              },
              {
                _id: 'StartChat',
                Type: 'ConnectAction',
                Props: {
                  ConnectActionType: 'StartChatContact',
                  Label: 'Start Chat',
                  StartChatContactProps: {
                    ContactFlowId: 'arn:aws:connect:us-east-1:050451381240:instance/3c1da07a-b3e7-45f0-b335-134d3d4b1a61/contact-flow/67d5abcb-0f82-4f18-bf40-0e9b0185b591',
                    ChatFields: {
                      CustomerDisplayName: { _linkedFormOutputName: 'first-name' },
                      InitialMessage:      { _linkedFormOutputName: 'company' }
                    },
                    Attributes: [
                      { Key: 'name',        Value: { _linkedFormOutputName: 'first-name' } },
                      { Key: 'company',     Value: { _linkedFormOutputName: 'company' } },
                      { Key: 'companyname', Value: { _linkedFormOutputName: 'company' } }
                    ]
                  }
                },
                Content: ['Connect Action Button']
              }
            ]
          }
        ]
      },
      actions: ['ActionSelected'],
      inputSchema: { type: 'object', properties: {}, required: [] }
    }
  }));

  amazon_connect('customLaunchBehavior', {
    skipIconButtonAndAutoLaunch: true,
    alwaysHideWidgetButton: true,
    programmaticLaunch: (function(launchCallback) {
      window.openZoomlogiChat = function() {
        try { launchCallback(); } catch (e) { console.error(e); }
      };
    })
  });
</script>
`;

    postscribe(rootEl, scriptContent);
    injectedRef.current = true;
  }, [decodedToken?.name, decodedToken?.company_name, decodedToken?.chatbot]);

  const useMobileControlTowerLayout =
    process.env.REACT_APP_CONTROL_TOWER_VIEW !== 'list';
  const layoutThemeVars = {
    '--zoomlogi-mobile-theme-color': theme.primary,
  } as React.CSSProperties;

  return (
    <div id='layout-connect' className='dashboard-container'>
      <div
        className={`sidebar ${
          useMobileControlTowerLayout && 'responsive-sidebar'
        } `}
        // eslint-disable-next-line react/forbid-dom-props -- sidebar colour comes from the active theme at run time
        style={{ backgroundColor: theme.primary }}
      >
        <CustomSidebar
          reportModalOpen={dataModalOpen}
          setReportModalOpen={setDataModalOpen}
        />
      </div>

      <div
        className={`content ${
          useMobileControlTowerLayout && 'responsive-content'
        } `}
      >
        <div
          className={`header ${
            useMobileControlTowerLayout ? 'mobile-table-header' : ''
          }`}
          // eslint-disable-next-line react/forbid-dom-props -- theme vars and preview colour are set at run time
          style={{
            ...layoutThemeVars,
            backgroundColor: showPreview ? '#2C4BE6' : undefined,
            height: showPreview ? '52px' : '64px',
          }}
        >
          <ResponsiveHeader
            useMobileMenu={useMobileControlTowerLayout}
            onOpenHelpCenter={toggleHelpCenter}
            helpCenterOpen={helpCenterOpen}
            insightChatOpen={sidePanel?.name === 'insight'}
            insightChatQuestion={sidePanel?.question}
            onOpenInsightChat={() => openInsightChat()}
            onCloseInsightChat={() => closeSidePanel('insight')}
            // Drop the handed-off question once sent, so it isn't sent twice.
            onInsightChatQuestionSent={() =>
              setSidePanel((prev) =>
                prev?.name === 'insight' ? { name: 'insight' } : prev
              )
            }
          />
        </div>

        <div
          className={`main ${useMobileControlTowerLayout && 'is-table-view'} ${
            isShipmentRoute ? ' main-shipments' : ''
          } ${helpCenterOpen ? 'help-center-push' : ''}`}
        >
          {children}
        </div>
      </div>

      <HelpCenterPanel
        open={helpCenterOpen}
        dataModalOpen={dataModalOpen}
        onClose={() => closeSidePanel('help')}
        onOpen={(source) => setSidePanel({ name: 'help', source })}
        openSource={sidePanel?.source}
        onAskInsightChat={openInsightChat}
      />
    </div>
  );
}

export default DashboardLayout;
